import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFetch } from "../../../utils/hooks/useFetch";
import { useToastInfo } from "../../../utils/hooks/useToastInfo";
import { getQueryParams } from "../../../utils/helpers";
import {
  getNotficationServiceConfig,
  clearNotificationsServiceConfig,
  updateNotificationStatusServiceConfig,
} from "../../../utils/services/notifications";
import moment from "moment";
import { translate } from "../../../utils/translations/translate";
import { useCachedResults } from "../../../utils/hooks/useCachedResults";
import { generateNotificationsReport } from "../../../utils/reports";
import { useUpdateCount } from "../../Header/updateCount";
const DEFAULT_PAGE_SIZE = 25;
export const useNotifications = () => {
  const { isLoading, performAction } = useFetch();
  const { toastInfo, setToastInfo } = useToastInfo();
  const gridRef = useRef();
  const { getCachedResult, setCachedResult } = useCachedResults();
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageNumber, setPageNumber] = useState(1);
  const [notiifcationsList, setNotificationsList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const updateCount = useUpdateCount();
  const resizeState = useRef(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [filterParams, setFilterParams] = useState({
    subject: "",
    isViewed: "",
    notificationType: "",
  });
  const currentParams = useMemo(() => {
    const { subject, isViewed, notificationType } = filterParams;
    return {
      PageNo: pageNumber,
      PageSize: pageSize,
      Subject: subject,
      IsViewed: isViewed,
      NotificationType: notificationType,
    };
  }, [pageNumber, pageSize, filterParams]);
  const handleRowSelection = (id, data, selected) => {
    setSelectedRows((prev) => {
      const newList = [...prev];
      const prevIndex = newList.findIndex((item) => item.id === id);
      if (!selected && prevIndex > -1) {
        newList.splice(prevIndex, 1);
      } else {
        newList.push(data);
      }
      return newList;
    });
  };
  const handlePaginationChange = useCallback(({ page, pageSize }) => {
    setPageNumber(page);
    setPageSize(pageSize);
  }, []);
  const updateNotificationReadStatus = useCallback(async () => {
    try {
      if (selectedRows.length === 0) {
        setToastInfo({
          message: translate("labels.selectRecordsForAction"),
          type: "info",
          autoExpire: true,
        });
        return false;
      }
      const recordsToUpdate = selectedRows
        .filter((row) => !row.isRead)
        .map((row) => row.id);
      if (recordsToUpdate.length === 0) {
        setToastInfo({
          message: translate("labels.noRecordsToUpdate"),
          type: "info",
          autoExpire: true,
        });
        return false;
      }
      const { url, options } =
        updateNotificationStatusServiceConfig(recordsToUpdate);
      const response = await performAction(url, options);
      if (response?.StatusCode === 200) {
        setNotificationsList((prevList) => {
          const newList = [...prevList];
          recordsToUpdate.forEach((record) => {
            const itemIndex = newList.findIndex((item) => item.id === record);
            if (itemIndex > -1) {
              newList[itemIndex].isRead = true;
              newList[itemIndex].status = "Read";
            }
          });
          return newList;
        });
        return setToastInfo({
          message: translate("labels.markAsReadSuccess"),
          type: "success",
          autoExpire: true,
        });
      }
      throw new Error("Unable to proceed with action");
    } catch (e) {
      console.error("Error updating Status");
      setToastInfo({
        message: e.message,
        type: "error",
        autoExpire: true,
      });
    } finally {
      window.scrollTo({ top: 0, behavior: "smooth" });
      updateCount();
    }
  }, [performAction, setToastInfo, selectedRows]);
  const getNotificationsResponseData = useCallback(
    async (additionalParams) => {
      try {
        const queryParams = getQueryParams(additionalParams);
        const { url, options } = getNotficationServiceConfig(queryParams);
        const response = await performAction(url, options);
        return response;
      } catch (e) {
        console.error("Error fetching notification list");
        setToastInfo({
          message: e.message,
          type: "error",
          autoExpire: true,
        });
      }
    },
    [performAction, setToastInfo]
  );
  const fetchNotifications = useCallback(
    async (additionalParams) => {
      try {
        const response = await getNotificationsResponseData(additionalParams);
        if (Array.isArray(response?.Result)) {
          const formattedNotificationList = response.Result.map(
            (notification) => {
              const {
                NotificationID,
                NotificationType,
                Subject,
                NotificationFrom,
                IsViewed,
                CreatedOn,
                TotalRecords,
              } = notification;
              return {
                id: NotificationID,
                messageType: NotificationType,
                status: IsViewed ? "Read" : "Unread",
                sender: NotificationFrom,
                message: Subject,
                isRead: IsViewed,
                records: TotalRecords,
                date: moment(CreatedOn).format("MM/DD/YYYY hh:mm A"),
              };
            }
          );
          const totalCount = formattedNotificationList[0]?.records || 0;
          setTotalRecords(totalCount);
          return setNotificationsList(formattedNotificationList);
        }
        throw new Error("Uanble to fetch notifications");
      } catch (e) {
        console.error("Error fetching notification list");
        setToastInfo({
          message: e.message,
          type: "error",
          autoExpire: true,
        });
      }
    },
    [setToastInfo, getNotificationsResponseData]
  );
  const clearSelected = async () => {
    try {
      const selectedRecords = selectedRows.map((row) => row.id);
      if (selectedRecords.length === 0) {
        setToastInfo({
          message: translate("labels.selectRecordsForAction"),
          type: "info",
          autoExpire: true,
        });
        return false;
      }
      const { url, options } = clearNotificationsServiceConfig(selectedRecords);
      const respone = await performAction(url, options);
      if (respone?.StatusCode === 200) {
        await fetchNotifications(currentParams);
        setToastInfo({
          message: translate("labels.clearSuccessToast"),
          type: "success",
          autoExpire: true,
        });
        return true;
      }
      throw new Error("Unable to perform action please try again");
    } catch (e) {
      setToastInfo({
        message: e.message,
        type: "error",
        autoExpire: true,
      });
    } finally {
      window.scrollTo({ top: 0, behavior: "smooth" });
      updateCount();
    }
  };
  const handleToastClose = useCallback(() => {
    setToastInfo(null);
  }, [setToastInfo]);
  const onRefreshNotifications = () => {
    fetchNotifications(currentParams);
  };
  const filterSelectedRecordsToDownload = (response) => {
    const transformResponse = (res) => {
      return res.map((record) => {
        const newRecord = Object.assign({}, record);
        delete newRecord.id;
        return newRecord;
      });
    };
    const selectedRecords =
      gridRef.current?.gridAPi.getSelectedRows()?.map((record) => record.id) ||
      [];
    if (selectedRecords.length) {
      const filteredRecords = response.filter((record) =>
        selectedRecords.includes(record.id)
      );
      return transformResponse(filteredRecords);
    }
    return transformResponse(response);
  };
  const handleNotificationDownload = async () => {
    try {
      const modifiedParams = Object.assign({}, currentParams);
      delete modifiedParams.PageNo;
      delete modifiedParams.PageSize;
      // const cachedResponse = getCachedResult(JSON.stringify(modifiedParams));
      // if (cachedResponse) {
      //   generateNotificationsReport(
      //     filterSelectedRecordsToDownload(cachedResponse)
      //   );
      //   return;
      // }
      const notificationResponse = await getNotificationsResponseData(
        modifiedParams
      );
      if (Array.isArray(notificationResponse?.Result)) {
        const listToDownload = notificationResponse.Result.map(
          (notification) => {
            const {
              NotificationID,
              NotificationType,
              Subject,
              NotificationFrom,
              IsViewed,
              CreatedOn,
            } = notification;
            return {
              id: NotificationID,
              Message_Type: NotificationType,
              Sender: NotificationFrom,
              Message: Subject,
              Status: IsViewed ? "Read" : "Unread",
              Recieved: moment(CreatedOn).format("MM/DD/YYYY hh:mm A"),
            };
          }
        );
        if (listToDownload.length) {
          setCachedResult(JSON.stringify(modifiedParams), listToDownload);
        }
        generateNotificationsReport(
          filterSelectedRecordsToDownload(listToDownload)
        );
      }
    } catch (e) {
      setToastInfo({
        message: "Sorry,Unable to Download the Notifications List",
        type: "error",
        autoExpire: true,
      });
    }
  };
  const onRequestNotificationWithAdditonalParams = useCallback((newParams) => {
    setFilterParams(newParams);
    setPageNumber(1);
    setPageSize(DEFAULT_PAGE_SIZE);
  }, []);
  useEffect(() => {
    fetchNotifications(currentParams);
  }, [fetchNotifications, currentParams]);
  return {
    notiifcationsList,
    totalRecords,
    toastInfo,
    pageSize,
    pageNumber,
    isLoading,
    selectedRows,
    resizeState,
    gridRef,
    handleRowSelection,
    clearSelected,
    handleToastClose,
    updateNotificationReadStatus,
    handlePaginationChange,
    onRefreshNotifications,
    handleNotificationDownload,
    onRequestNotificationWithAdditonalParams,
  };
};
